import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiGet } from "../lib/api";
import Masthead from "../components/Masthead";
import Kpi from "../components/Kpi";
import Panel from "../components/Panel";
import { fmtNum, statusClass } from "../lib/format";

function statusTone(s) {
  if (s === "On track" || s === "Compliant") return "var(--cv-green)";
  if (s === "At risk" || s === "In Review" || s === "In Progress") return "#D55816";
  if (s === "Blocked" || s === "Non-compliant") return "var(--cv-crimson)";
  return "var(--cv-slate)";
}

function severityTone(s) {
  if (s === "Critical" || s === "High" || s === "P0") return "var(--cv-crimson)";
  if (s === "Medium" || s === "P1") return "#D55816";
  return "var(--cv-slate)";
}

export default function Projects() {
  const [d, setD] = useState(null);
  const nav = useNavigate();
  useEffect(() => { apiGet("/projects/summary").then(setD); }, []);
  if (!d) return <div className="p-8 opacity-60">Loading…</div>;
  const k = d.kpis;
  const postureTone = d.posture_score >= 85 ? "var(--cv-green)"
    : d.posture_score >= 70 ? "#D55816" : "var(--cv-crimson)";

  return (
    <>
      <Masthead
        module="projects"
        eyebrow="Project Management · Security Delivery + PMO"
        title="Project Management"
        subtitle="Security tool roll-outs · sprint velocity · milestones · risks · budget utilisation"
        meta={<div>{k.active_projects} active projects · {k.milestones_this_qtr} milestones this qtr<br />as at {d.as_at}</div>}
      />
      <div className="px-[26px] py-5 flex-1">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-5">
          <button type="button" data-testid="proj-posture"
                  onClick={() => nav("/drill/sec_projects")}
                  className="cv-panel text-left hover:shadow-lg transition-shadow"
                  style={{ borderLeft: `4px solid ${postureTone}`, cursor: "pointer" }}>
            <div className="cv-kpi-label">Delivery Posture</div>
            <div className="flex items-end gap-3 mt-3">
              <div className="font-serif-display leading-none" style={{ color: postureTone, fontSize: 60 }}>
                {d.posture_score}
              </div>
              <div className="pb-2">
                <div className="text-[13px] small-caps font-bold" style={{ color: "var(--cv-charcoal-soft)" }}>/ 100</div>
                <div className="text-[11.5px]" style={{ color: "var(--cv-charcoal-soft)" }}>target {d.posture_target}</div>
              </div>
            </div>
            <div className="cv-kpi-sub mt-2">
              Composite of on-track rate, sprint velocity attainment &amp; milestone completion.
            </div>
          </button>
          <Kpi testid="proj-active" label="Active projects" value={k.active_projects}
               sub={`${k.on_track} on track · ${k.at_risk} at risk · ${k.blocked} blocked`}
               onClick={() => nav("/drill/sec_projects")} />
          <Kpi testid="proj-sprint" label="Sprint completion" value={`${k.sprint_completion_pct}%`}
               sub={`velocity ${k.velocity_pts} / ${k.velocity_target} pts`}
               onClick={() => nav("/drill/project_stories?sprint_status=In Progress")} />
          <Kpi testid="proj-milestones" label="Milestones this quarter" value={`${k.milestones_done}/${k.milestones_this_qtr}`}
               sub={`budget utilisation ${k.budget_utilisation_pct}%`}
               onClick={() => nav("/drill/sec_projects")} />
        </div>

        {/* Security Programme Roll-outs */}
        <div className="cv-section-label">Security Programme Roll-outs</div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-5">
          {d.sec_projects.map((p) => {
            const gap = p.target_pct - p.rollout_pct;
            const barTone = gap <= 5 ? "var(--cv-green)" : gap <= 15 ? "#D55816" : "var(--cv-crimson)";
            return (
              <button type="button" key={p.key} data-testid={`sec-proj-${p.key}`}
                      onClick={() => nav(`/drill/project_stories?project_key=${p.key}`)}
                      className="cv-panel text-left hover:shadow-lg transition-shadow"
                      style={{ cursor: "pointer" }}>
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <div className="font-bold small-caps text-[13.5px]" style={{ color: "var(--cv-crimson)" }}>{p.name}</div>
                    <div className="text-[11.5px] mt-0.5" style={{ color: "var(--cv-charcoal-soft)" }}>{p.phase} · owner {p.owner}</div>
                  </div>
                  <span className="text-[10.5px] small-caps font-bold shrink-0" style={{ color: statusTone(p.status) }}>{p.status}</span>
                </div>
                <div className="flex items-end gap-2 mb-2">
                  <div className="font-serif-display text-[36px] leading-none" style={{ color: barTone }}>{p.rollout_pct}%</div>
                  <div className="text-[11.5px] small-caps mb-1" style={{ color: "var(--cv-charcoal-soft)" }}>target {p.target_pct}%</div>
                </div>
                <div className="h-2 rounded-full overflow-hidden" style={{ background: "var(--cv-line)" }}>
                  <div className="h-full rounded-full" style={{ width: `${p.rollout_pct}%`, background: barTone }} />
                </div>
                <div className="text-[11.5px] mt-2" style={{ color: "var(--cv-charcoal-soft)" }}>
                  Risk: <strong style={{ color: severityTone(p.risk) }}>{p.risk}</strong>
                </div>
              </button>
            );
          })}
        </div>

        {/* Sprint board + Stories + Milestones */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-5">
          <Panel testid="panel-sprint-board" title="Sprint Board" subtitle="Backlog · In progress · Review · Done">
            <div className="grid grid-cols-2 gap-2 mb-3">
              {d.sprint_board.map((c) => {
                const statusFilter = c.col.replace(/ /g, " "); // exact match
                return (
                  <button type="button" key={c.col}
                          data-testid={`sprint-col-${c.col.toLowerCase().replace(/\s+/g,'-')}`}
                          onClick={() => nav(`/drill/project_stories?sprint_status=${encodeURIComponent(statusFilter)}`)}
                          className="cv-tile text-left hover:shadow-md transition-shadow"
                          style={{ cursor: "pointer" }}>
                    <div className="cv-tile-label">{c.col}</div>
                    <div className="cv-tile-value">{c.count}</div>
                  </button>
                );
              })}
            </div>
            <div className="text-[11.5px]" style={{ color: "var(--cv-charcoal-soft)" }}>
              {k.open_stories} stories open · {k.in_progress} in progress · {k.done_this_sprint} done this sprint · {k.spillover} spillover
            </div>
          </Panel>

          <Panel testid="panel-stories" title="Top Priority Stories" subtitle="P0 &amp; P1 stories in flight">
            <div className="flex flex-col gap-1.5">
              {d.top_stories.map((s) => (
                <button type="button" key={s.key}
                        onClick={() => nav(`/drill/project_stories?priority=${encodeURIComponent(s.priority)}`)}
                        className="flex justify-between items-start gap-2 text-[12.5px] py-1.5 text-left hover:opacity-80 transition-opacity w-full"
                        style={{ background: "transparent", border: 0, borderBottom: "1px dashed var(--cv-line)", cursor: "pointer" }}>
                  <div className="min-w-0 flex-1">
                    <div className="font-bold" style={{ color: "var(--cv-charcoal)" }}>{s.key} · {s.title}</div>
                    <div className="text-[11px]" style={{ color: "var(--cv-charcoal-soft)" }}>{s.owner} · {s.points} pts</div>
                  </div>
                  <div className="text-right shrink-0">
                    <span className="text-[10.5px] small-caps font-bold" style={{ color: severityTone(s.priority) }}>{s.priority}</span>
                    <div className="text-[10.5px] small-caps mt-0.5" style={{ color: statusTone(s.status) }}>{s.status}</div>
                  </div>
                </button>
              ))}
            </div>
          </Panel>

          <Panel testid="panel-milestones" title="Milestones" subtitle="This quarter · upcoming">
            <div className="flex flex-col gap-1.5 mb-3">
              {d.milestones.map((m) => (
                <div key={m.name} className="flex justify-between items-center gap-2 text-[12.5px] py-1.5"
                     style={{ borderBottom: "1px dashed var(--cv-line)" }}>
                  <div className="min-w-0 flex-1">
                    <div className="font-bold" style={{ color: "var(--cv-charcoal)" }}>{m.name}</div>
                    <div className="text-[11px]" style={{ color: "var(--cv-charcoal-soft)" }}>due {m.due}</div>
                  </div>
                  <span className="text-[10.5px] small-caps font-bold shrink-0" style={{ color: statusTone(m.status) }}>{m.status}</span>
                </div>
              ))}
            </div>
            <div className="text-[11.5px] small-caps font-bold tracking-[0.1em] mb-1" style={{ color: "var(--cv-dark-grey)" }}>
              Programme risks
            </div>
            <div className="flex flex-col gap-1">
              {d.risks.map((r) => (
                <div key={r.title} className="text-[12px] py-1" style={{ color: "var(--cv-charcoal)" }}>
                  <span className="font-bold" style={{ color: severityTone(r.severity) }}>[{r.severity}]</span> {r.title}
                </div>
              ))}
            </div>
          </Panel>
        </div>
      </div>
    </>
  );
}
